import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  FileText, Lock, Unlock, Upload, Download, Plus, Trash2, Save, Loader2, Edit, FileLock2, Building2, AlertTriangle,
} from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable Legal-folder UI panel.
 *
 * Used by:
 *   - /operator/legal (props: scope='operator', clubId=null)
 *   - ClubAdminDashboard "Legal" tab (props: scope='club', clubId=<slug>)
 */
export default function LegalDocumentsPanel({ scope = 'operator', clubId = null, clubName = null }) {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // doc currently being edited
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newDoc, setNewDoc] = useState({ title: '', body: '' });

  const isOperatorAdmin = !!(user?.is_admin || user?.is_operator_admin);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = clubId ? { club_id: clubId } : {};
      const res = await axios.get(`${API}/legal/documents`, { params });
      setDocs(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleCreate = async () => {
    if (!newDoc.title.trim()) { toast.error('Title is required'); return; }
    try {
      const res = await axios.post(`${API}/legal/documents`, {
        title: newDoc.title.trim(),
        body: newDoc.body,
        club_id: clubId,
      });
      setDocs((prev) => [res.data, ...prev]);
      setCreating(false);
      setNewDoc({ title: '', body: '' });
      setEditing(res.data);
      toast.success('Document created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create');
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      const res = await axios.patch(`${API}/legal/documents/${editing.id}`, {
        title: editing.title,
        body: editing.body,
      });
      setDocs((prev) => prev.map((d) => (d.id === res.data.id ? res.data : d)));
      setEditing(null);
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/legal/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.kind === 'file' ? (doc.file_name || 'document') : `${doc.title}.md`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const title = window.prompt('Title for this document:', file.name);
    if (!title) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title);
      if (clubId) fd.append('club_id', clubId);
      const res = await axios.post(`${API}/legal/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocs((prev) => [res.data, ...prev]);
      toast.success(`Uploaded "${title}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleLock = async (doc, lockState) => {
    if (!lockState) {
      const ok = window.confirm('Unlock this document? Only operator admins can unlock.');
      if (!ok) return;
    } else {
      const ok = window.confirm(
        'Lock this document? It will become read-only — no edits, no overwrite, no deletion until an operator admin unlocks it.'
      );
      if (!ok) return;
    }
    try {
      await axios.post(`${API}/legal/documents/${doc.id}/lock?locked=${lockState}`);
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, locked: lockState } : d)));
      toast.success(lockState ? 'Locked' : 'Unlocked');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not toggle lock');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/legal/documents/${doc.id}`);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success('Deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  const headerLabel = scope === 'club' ? `${clubName || 'Club'} legal documents` : 'Operator legal documents';

  return (
    <div className="space-y-4" data-testid={`legal-panel-${scope}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-heading font-semibold flex items-center gap-2">
            <FileLock2 className="w-5 h-5 text-amber-700" /> {headerLabel}
          </h2>
          <p className="text-sm text-muted-foreground">
            Drafts and signed contracts. Edit drafts in the browser; lock signed contracts to make them read-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer">
            <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="legal-upload-input" />
            <span>
              <Button variant="outline" size="sm" disabled={uploading} asChild>
                <span data-testid="legal-upload-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  Upload
                </span>
              </Button>
            </span>
          </label>
          <Button size="sm" onClick={() => setCreating(true)} data-testid="legal-create-btn">
            <Plus className="w-4 h-4 mr-1" /> New draft
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : docs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="text-center py-10 text-muted-foreground">
            No documents yet. Create a draft or upload a signed contract.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {docs.map((doc) => (
            <Card key={doc.id} className="overflow-hidden" data-testid={`legal-doc-${doc.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${doc.kind === 'file' ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700'}`}>
                    {doc.kind === 'file' ? <FileLock2 className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base font-heading break-words">{doc.title}</CardTitle>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <Badge variant="outline" className="text-[10px]">{doc.kind === 'file' ? 'Uploaded file' : 'Editable draft'}</Badge>
                      {doc.locked && (
                        <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-700 bg-rose-50">
                          <Lock className="w-3 h-3 mr-0.5" /> Locked
                        </Badge>
                      )}
                      {doc.club_id && scope === 'operator' && (
                        <Badge variant="outline" className="text-[10px]"><Building2 className="w-3 h-3 mr-0.5" />Club: {doc.club_id}</Badge>
                      )}
                      {doc.file_size && (
                        <span>{(doc.file_size / 1024).toFixed(1)} KB</span>
                      )}
                      <span>Updated {new Date(doc.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {doc.kind === 'markdown' && !doc.locked && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(doc)} data-testid={`legal-edit-${doc.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleDownload(doc)} data-testid={`legal-download-${doc.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    {doc.locked ? (
                      isOperatorAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => handleLock(doc, false)} data-testid={`legal-unlock-${doc.id}`}>
                          <Unlock className="w-4 h-4 text-amber-600" />
                        </Button>
                      )
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => handleLock(doc, true)} data-testid={`legal-lock-${doc.id}`}>
                        <Lock className="w-4 h-4" />
                      </Button>
                    )}
                    {!doc.locked && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(doc)} data-testid={`legal-delete-${doc.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle>New legal draft</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Title (e.g. Rescue Class Agreement — DRAFT)"
              value={newDoc.title}
              onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
              data-testid="legal-new-title"
            />
            <Textarea
              placeholder="Markdown body…"
              rows={10}
              value={newDoc.body}
              onChange={(e) => setNewDoc({ ...newDoc, body: e.target.value })}
              className="font-mono text-sm"
              data-testid="legal-new-body"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="legal-create-submit"><Save className="w-4 h-4 mr-1" /> Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Edit document</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                data-testid="legal-edit-title"
              />
              <Textarea
                rows={20}
                value={editing.body || ''}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                className="font-mono text-sm"
                data-testid="legal-edit-body"
              />
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                Markdown is rendered when downloaded. Save to persist; Cancel to discard.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="legal-edit-save"><Save className="w-4 h-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
